import { unwrap } from "@majourneys/api-client";
import { api } from "../../../lib/api";
import type {
  DiscoverCity,
  DiscoverCityTripPreset,
  DiscoverDetail,
  DiscoverItem,
  DiscoverSegment,
  DiscoverTheme,
} from "@majourneys/discover";

interface MajourneysActivity {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  overview?: string;
  images: string[];
  price: number;
  rating: number;
  latitude?: number | null;
  longitude?: number | null;
  category: { id: string; name: string; slug: string } | null;
  city: { id: string; name: string; slug: string };
  mode?: "bookable" | "explore";
  segment?: string;
  mentions?: number;
  /** Editorial fields populated for explore-mode rows. */
  template?: string | null;
  contentBlocks?: Record<string, unknown>;
  bodyMarkdown?: string | null;
  heroImageUrl?: string | null;
  galleryImageUrls?: string[];
  facts?: string[];
  faqs?: { question: string; answer: string }[];
  overviewSections?: { title: string; body: string }[];
}


function inferSegment(label: string, title?: string): DiscoverSegment {
  const n = `${label} ${title ?? ""}`.toLowerCase();
  if (/(restaurant|food|dining|cafe|café)/.test(n)) return "restaurants";
  if (/(stay|hotel|riad|lodge|villa|guesthouse)/.test(n)) return "stays";
  if (/(spa|hammam|wellness)/.test(n)) return "spas";
  if (/\bgolf\b/.test(n)) return "golf";
  if (/(beach club|plage priv|pool club|day pass)/.test(n)) return "beach-clubs";
  if (/(nightclub|night club|nightlife|discoth)/.test(n)) return "nightlife";
  if (/(beach|park|garden|gate|monument|landmark|mosque|museum|kasbah|medina|tomb|fort|ruin|attraction)/.test(n))
    return "locations";
  return "bookable";
}

type SegLang = "en" | "fr" | "ar" | "es" | "it" | "de" | "nl";

const SEG_LANGS: readonly SegLang[] = ["en", "fr", "ar", "es", "it", "de", "nl"];

const SEGMENT_LABELS: Record<Exclude<DiscoverSegment, "bookable" | "activities">, Record<SegLang, string>> = {
  restaurants: { en: "RESTAURANT", fr: "RESTAURANT", ar: "مطعم", es: "RESTAURANTE", it: "RISTORANTE", de: "RESTAURANT", nl: "RESTAURANT" },
  stays: { en: "STAY", fr: "SÉJOUR", ar: "إقامة", es: "ALOJAMIENTO", it: "SOGGIORNO", de: "UNTERKUNFT", nl: "VERBLIJF" },
  spas: { en: "SPA", fr: "SPA", ar: "سبا", es: "SPA", it: "SPA", de: "SPA", nl: "SPA" },
  locations: { en: "ATTRACTION", fr: "ATTRACTION", ar: "معلم", es: "ATRACCIÓN", it: "ATTRAZIONE", de: "ATTRAKTION", nl: "ATTRACTIE" },
  guides: { en: "GUIDE", fr: "GUIDE", ar: "دليل", es: "GUÍA", it: "GUIDA", de: "GUIDE", nl: "GIDS" },
  golf: { en: "GOLF", fr: "GOLF", ar: "غولف", es: "GOLF", it: "GOLF", de: "GOLF", nl: "GOLF" },
  nightlife: { en: "NIGHTLIFE", fr: "VIE NOCTURNE", ar: "حياة ليلية", es: "VIDA NOCTURNA", it: "VITA NOTTURNA", de: "NACHTLEBEN", nl: "NACHTLEVEN" },
  "beach-clubs": { en: "BEACH CLUB", fr: "BEACH CLUB", ar: "نادي شاطئ", es: "BEACH CLUB", it: "BEACH CLUB", de: "BEACHCLUB", nl: "BEACHCLUB" },
};

function segmentLabel(segment: DiscoverSegment, category: string, lang?: string): string {
  const base = lang?.split("-")[0];
  const l: SegLang = (SEG_LANGS as readonly string[]).includes(base ?? "") ? (base as SegLang) : "en";
  if (segment in SEGMENT_LABELS) {
    return SEGMENT_LABELS[segment as keyof typeof SEGMENT_LABELS][l];
  }
  return category.toUpperCase();
}

/**
 * Map a segment to the desktop tab strip. Each item lands in "for-you" plus
 * exactly ONE category tab so the tabs are mutually distinct — switching tabs
 * always changes the list.
 *
 * "Attractions" means actual attractions/landmarks (explore `locations`:
 * monuments, gardens, museums, beaches), matching the mobile "Attractions"
 * bucket. Bookable experiences (quads, jet ski, tours, pools, shows, spas) and
 * editorial guides are "Things to do". Restaurants and stays own their own tab.
 *
 * The previous mapping tagged every bookable activity with "attractions" too,
 * so Attractions === Things to do and clicking it looked like a no-op.
 */
export function inferTabs(segment: DiscoverSegment): readonly string[] {
  if (segment === "restaurants") return ["for-you", "restaurants"];
  if (segment === "stays") return ["for-you", "stays"];
  if (segment === "locations") return ["for-you", "attractions"];
  // Both are places you go rather than tours you book, but neither is an
  // "attraction" — they sit with the bookable experiences instead.
  if (segment === "nightlife" || segment === "beach-clubs") return ["for-you", "things-to-do"];
  // activities, bookable, spas, guides → bookable experiences / things to do
  return ["for-you", "things-to-do"];
}

const VALID_SEGMENTS: ReadonlySet<string> = new Set([
  "activities",
  "bookable",
  "restaurants",
  "stays",
  "spas",
  "locations",
  "guides",
  "golf",
  "nightlife",
  "beach-clubs",
]);

function isDiscoverSegment(s: string | undefined): s is DiscoverSegment {
  return typeof s === "string" && VALID_SEGMENTS.has(s);
}

/**
 * Resolve a discover segment for an item.
 *
 * The API stamps a generic `segment: "bookable"` on every bookable row, so that
 * value must NOT win over category inference — otherwise villas, hotels and
 * bookable restaurants all collapse into "bookable" and show up under the
 * Activities filter on the map/sheet. A *specific* server segment
 * (restaurants/stays/spas/locations/guides/activities) still wins; only the
 * generic "bookable" (and a missing segment) gets refined from the category.
 */
export function resolveSegment(
  serverSegment: string | null | undefined,
  categoryName: string,
  title: string | undefined,
  mode: string | null | undefined,
): DiscoverSegment {
  // Bookable rows are ALL tagged segment="activities" by the API regardless of
  // what they actually are (villa, quad, spa, boat…), so that value is useless
  // for categorisation — classify by the category instead. Only stays
  // (villas/riads/hotels) and spas leave the Activities bucket; every other
  // bookable (tours, quads, boats, cooking, crafts, pools…) stays an Activity.
  if (mode === "bookable") {
    const c = categoryName.toLowerCase();
    if (/villa|riad|\bhotel\b|lodge|guesthouse|guest ?house|apartment/.test(c)) return "stays";
    if (/spa|hammam|wellness/.test(c)) return "spas";
    return "activities";
  }
  // Explore rows are correctly segmented by the API (locations/restaurants/
  // spas/guides). Trust a specific segment; refine a generic/missing one.
  const server = serverSegment ?? undefined;
  if (isDiscoverSegment(server) && server !== "bookable") return server;
  const inferred = inferSegment(categoryName, title);
  return inferred === "bookable" ? "locations" : inferred;
}

function inferTheme(segment: DiscoverSegment): DiscoverTheme {
  if (segment === "restaurants") return "orange";
  if (segment === "stays") return "cream";
  if (segment === "spas") return "purple";
  if (segment === "nightlife") return "purple";
  if (segment === "beach-clubs") return "cream";
  return "navy";
}

// For explore-mode rows (restaurants, stays, spas, attractions), the API
// `price` column is unused — pricing lives in the editorial `priceRange`
// content block as a localized string list (e.g. ["$$", "MAD 200–400 per
// person"]). Surface the first entry as the pin's priceLabel so the map
// preview shows something useful instead of falling back to "Pay on arrival".
function extractExplorePriceLabel(
  contentBlocks: Record<string, unknown> | undefined,
  lang?: string,
): string | null {
  if (!contentBlocks || typeof contentBlocks !== "object") return null;
  const block = contentBlocks as Record<string, unknown>;
  // priceRange may be nested directly or under a typed block key. Try both.
  const candidates: unknown[] = [block.priceRange];
  for (const key of Object.keys(block)) {
    const value = block[key];
    if (value && typeof value === "object" && "priceRange" in value) {
      candidates.push((value as Record<string, unknown>).priceRange);
    }
  }
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const localized = candidate as Record<string, unknown>;
    const list =
      (lang && Array.isArray(localized[lang]) && (localized[lang] as unknown[])) ||
      (Array.isArray(localized.en) && localized.en as unknown[]) ||
      (Array.isArray(localized.fr) && localized.fr as unknown[]) ||
      (Array.isArray(localized.ar) && localized.ar as unknown[]) ||
      null;
    if (!list) continue;
    const first = list.find((v): v is string => typeof v === "string" && v.trim().length > 0);
    if (first) return first.trim();
  }
  return null;
}

function mapActivity(activity: MajourneysActivity, lang?: string): DiscoverItem {
  const category = activity.category?.name || "Activity";
  // Server-provided segment wins over inferred segment when present.
  const segment = resolveSegment(activity.segment, category, activity.title, activity.mode);
  const isExplore = activity.mode === "explore";
  const heroFromUrl = activity.heroImageUrl ?? null;
  const galleryFromUrls = activity.galleryImageUrls ?? [];
  const image = heroFromUrl ?? activity.images[0] ?? "";
  const gallery = galleryFromUrls.length > 0 ? galleryFromUrls : activity.images;
  // Price priority: numeric `price` for bookables → editorial `priceRange`
  // content block (covers restaurants/stays/spas + bookables without a fixed
  // price). Falls back to null so the UI renders nothing rather than a stale
  // "MAD 0".
  const numericPriceLabel =
    !isExplore && activity.price > 0 ? `MAD ${Math.round(activity.price)}` : null;
  // No price on a restaurant, bookable or not (owner rule): tables are free.
  const editorialPriceLabel =
    segment === "restaurants" ? null : extractExplorePriceLabel(activity.contentBlocks, lang);
  const priceLabel = numericPriceLabel ?? editorialPriceLabel ?? null;
  const ratingStr = activity.rating > 0 ? activity.rating.toFixed(1) : "";
  const mode: "bookable" | "explore" = activity.mode ?? "bookable";

  return {
    id: `mj-${activity.id}`,
    mode,
    segment,
    tabs: inferTabs(segment),
    sourceSlug: activity.slug,
    title: activity.title,
    subtitle: segmentLabel(segment, category, lang),
    location: activity.city.name || "",
    citySlug: activity.city.slug,
    categorySlug: activity.category?.slug ?? undefined,
    description: activity.subtitle ?? "",
    headerDescription: activity.overview ?? "",
    rating: ratingStr,
    mentions: activity.mentions ?? 0,
    priceLabel,
    pinLabel: category,
    image,
    gallery,
    latitude: typeof activity.latitude === "number" ? activity.latitude : null,
    longitude: typeof activity.longitude === "number" ? activity.longitude : null,
    mapX: 50,
    mapY: 50,
    mapIcon: segment === "restaurants" ? "ri-restaurant-line" : "ri-ticket-2-line",
    theme: inferTheme(segment),
    canShowLocation:
      typeof activity.latitude === "number" &&
      typeof activity.longitude === "number" &&
      Number.isFinite(activity.latitude) &&
      Number.isFinite(activity.longitude),
    locationEmbedUrl:
      typeof activity.latitude === "number" &&
      typeof activity.longitude === "number" &&
      Number.isFinite(activity.latitude) &&
      Number.isFinite(activity.longitude)
        ? `https://www.google.com/maps?q=${activity.latitude},${activity.longitude}&hl=${lang ?? "en"}&z=15&output=embed`
        : null,
    facts: activity.facts ?? [],
    questions: [],
    overviewSections: activity.overviewSections ?? [],
    availablePacks: [],
    faqs: activity.faqs ?? [],
    detailHref: null,
    template: activity.template ?? null,
    contentBlocks: activity.contentBlocks ?? {},
  };
}

export function getInitialDiscoverItems(): DiscoverItem[] {
  return [];
}

export async function fetchMajourneysDiscoverItems(lang?: string): Promise<DiscoverItem[]> {
  const { items } = await fetchMajourneysDiscoverIndex(lang);
  return items;
}

export interface DiscoverIndexRow {
  id: string;
  /** "stay" for a hotel from the stays catalogue; absent for activities. */
  kind?: "stay";
  slug: string;
  title: string;
  mode: "bookable" | "explore";
  segment: string | null;
  priceLabel: string | null;
  priceMad?: number | null;
  image: string;
  pinLabel: string;
  rating: number;
  mentions: number;
  latitude: number | null;
  longitude: number | null;
  city: { id: string; slug: string; name: string };
  category: { id: string; slug: string; name: string } | null;
  tags?: string[];
}

interface DiscoverIndexEnvelope {
  data: { items: DiscoverIndexRow[]; cities: DiscoverCity[] };
}

export function mapIndexRow(row: DiscoverIndexRow, lang?: string): DiscoverItem {
  const isStay = row.kind === "stay";
  const segment = isStay
    ? "stays"
    : // The category's slug first: it is English in every language, while
      // the name is translated, so an Arabic "المنتجعات الصحية" never
      // matched /spa/ and spas showed as plain places in Arabic.
      resolveSegment(
        row.segment,
        [row.category?.slug, row.category?.name].filter(Boolean).join(" ") || "Activity",
        row.title,
        row.mode,
      );
  // Build a Google Maps embed URL when we have coordinates so the location
  // tab shows an actual map instead of just the city name.
  const hasCoords =
    typeof row.latitude === "number" &&
    typeof row.longitude === "number" &&
    Number.isFinite(row.latitude) &&
    Number.isFinite(row.longitude);
  const locationEmbedUrl = hasCoords
    ? `https://www.google.com/maps?q=${row.latitude},${row.longitude}&hl=${lang ?? "en"}&z=15&output=embed`
    : null;
  return {
    id: `mj-${row.id}`,
    mode: row.mode,
    segment,
    tabs: inferTabs(segment),
    sourceSlug: row.slug,
    title: row.title,
    subtitle: segmentLabel(segment, row.category?.name ?? "Activity", lang),
    location: row.city.name,
    citySlug: row.city.slug,
    categorySlug: row.category?.slug,
    description: "",
    headerDescription: "",
    rating: row.rating > 0 ? row.rating.toFixed(1) : "",
    mentions: row.mentions,
    priceLabel: row.priceLabel,
    priceMad: row.priceMad ?? null,
    pinLabel: row.pinLabel || row.category?.name || "",
    image: row.image,
    gallery: [row.image],
    latitude: row.latitude,
    longitude: row.longitude,
    mapX: 50,
    mapY: 50,
    mapIcon: segment === "restaurants" ? "ri-restaurant-line" : "ri-ticket-2-line",
    theme: inferTheme(segment),
    canShowLocation: locationEmbedUrl !== null,
    locationEmbedUrl,
    facts: [],
    questions: [],
    overviewSections: [],
    availablePacks: [],
    faqs: [],
    // A hotel opens its own page, where it is booked and paid online, rather
    // than the side panel (see selectItem in @majourneys/discover).
    detailHref: isStay ? `/${lang ?? "en"}/stays/${row.city.slug}/${row.slug}` : null,
    template: null,
    contentBlocks: {},
    tags: row.tags ?? [],
  };
}

export async function fetchMajourneysDiscoverIndex(
  lang?: string,
): Promise<{ items: DiscoverItem[]; cities: DiscoverCity[] }> {
  const query: Record<string, string> = {};
  if (lang) query.lang = lang;
  const response = (await api.activities["discover-index"].get({ query })) as unknown as
    | DiscoverIndexEnvelope
    | { data: DiscoverIndexEnvelope };
  // Eden Treaty reports a failed request as `{ data: null, error }` rather
  // than throwing. Read as an empty catalogue, it put "0 picks" on the map;
  // thrown, Discover knows the count failed and hides it instead.
  const failed = (response as { error?: unknown }).error;
  if (failed) throw new Error("Could not load the Discover catalogue");
  // Eden Treaty wraps the body under `data` once; the API also wraps under
  // `{ data: ... }`. Unwrap defensively.
  const inner = (response as { data: unknown }).data as
    | DiscoverIndexEnvelope["data"]
    | DiscoverIndexEnvelope
    | null;
  const payload =
    inner && "items" in (inner as Record<string, unknown>)
      ? (inner as DiscoverIndexEnvelope["data"])
      : ((inner as DiscoverIndexEnvelope | null)?.data ?? { items: [], cities: [] });
  const items = payload.items
    .map((row) => mapIndexRow(row, lang))
    .filter((it) => it.image && it.image.length > 0);
  // The API now returns discoverHeroLabel/discoverMapEmbedUrl/discoverTripPresets
  // per city. Older API responses omit them; fall back to safe defaults so the
  // UI never receives undefined here.
  const cities: DiscoverCity[] = payload.cities.map((c) => {
    const presets = (c as { discoverTripPresets?: unknown }).discoverTripPresets;
    return {
      ...c,
      discoverHeroLabel:
        typeof (c as { discoverHeroLabel?: unknown }).discoverHeroLabel === "string"
          ? ((c as { discoverHeroLabel: string }).discoverHeroLabel)
          : "",
      discoverMapEmbedUrl:
        typeof (c as { discoverMapEmbedUrl?: unknown }).discoverMapEmbedUrl === "string"
          ? ((c as { discoverMapEmbedUrl: string }).discoverMapEmbedUrl)
          : "",
      discoverTripPresets: Array.isArray(presets)
        ? ([...presets] as DiscoverCityTripPreset[])
        : [],
    };
  });
  return { items, cities };
}

interface DiscoverDetailDto {
  id: string;
  slug: string;
  subtitle?: string;
  overview?: string;
  gallery?: string[];
  template?: string | null;
  contentBlocks?: Record<string, unknown>;
  facts?: string[];
  faqs?: { question: string; answer: string }[];
  overviewSections?: { title: string; body: string }[];
}

export async function fetchDiscoverDetail(
  slug: string,
  lang?: string,
): Promise<DiscoverDetail> {
  const query: Record<string, string> = {};
  if (lang) query.lang = lang;
  const response = (await api.activities["discover-detail"]({ slug }).get({
    query,
  })) as unknown as { data: DiscoverDetailDto } | { data: { data: DiscoverDetailDto } };
  const inner = (response as { data: unknown }).data as
    | DiscoverDetailDto
    | { data: DiscoverDetailDto };
  const dto: DiscoverDetailDto =
    inner && "id" in (inner as Record<string, unknown>)
      ? (inner as DiscoverDetailDto)
      : (inner as { data: DiscoverDetailDto }).data;
  return {
    id: dto.id,
    slug: dto.slug,
    subtitle: dto.subtitle ?? "",
    overview: dto.overview ?? "",
    gallery: dto.gallery ?? [],
    template: dto.template ?? null,
    contentBlocks: dto.contentBlocks ?? {},
    facts: dto.facts ?? [],
    faqs: dto.faqs ?? [],
    overviewSections: dto.overviewSections ?? [],
  };
}

export async function fetchMajourneysDiscover(lang?: string): Promise<{
  items: DiscoverItem[];
  cities: DiscoverCity[];
}> {
  // Fetch home (carries prices + cities), explore-mode and bookable-mode
  // activity pages (which carry lat/lng) in parallel.
  const [homeResponse, exploreResponse, bookableResponse] = await Promise.all([
    api.home.get({ query: lang ? { lang } : {} }).then(unwrap),
    fetchActivitiesByMode("explore", lang),
    fetchActivitiesByMode("bookable", lang),
  ]);

  const data = homeResponse.data ?? {};
  const bookablePool: MajourneysActivity[] = [
    ...(data.heroActivities ?? []),
    ...(data.trendingActivities ?? []),
    ...(data.newActivities ?? []),
    ...(data.offers ?? []),
  ];

  // Build a slug-keyed coord index from the /activities responses so we can
  // enrich home-pool items (which don't expose lat/lng) before mapping.
  const coordsBySlug = new Map<string, { lat: number; lng: number }>();
  for (const a of [...exploreResponse, ...bookableResponse]) {
    if (
      typeof a.latitude === "number" &&
      typeof a.longitude === "number" &&
      a.slug
    ) {
      coordsBySlug.set(a.slug, { lat: a.latitude, lng: a.longitude });
    }
  }
  for (const a of bookablePool) {
    if (typeof a.latitude === "number" && typeof a.longitude === "number") continue;
    const c = a.slug ? coordsBySlug.get(a.slug) : undefined;
    if (c) {
      a.latitude = c.lat;
      a.longitude = c.lng;
    }
  }

  // The `/activities` list serializer doesn't carry the min-package price
  // (pricing is computed via a `prices` join only by `listActivities` in
  // explore-data, which feeds the home pool). Build a slug-keyed price index
  // from the home pool so /activities-only items still surface a price label.
  const priceBySlug = new Map<string, number>();
  for (const a of bookablePool) {
    if (typeof a.price === "number" && a.price > 0 && a.slug) {
      priceBySlug.set(a.slug, a.price);
    }
  }
  for (const a of [...exploreResponse, ...bookableResponse]) {
    if (typeof a.price === "number" && a.price > 0) continue;
    const p = a.slug ? priceBySlug.get(a.slug) : undefined;
    if (typeof p === "number") {
      a.price = p;
    }
  }

  // Merge order matters: /activities responses carry the full shape
  // (template, contentBlocks, facts, faqs, overviewSections), while the
  // /home pool returns the minimal shape (no editorial blocks). Iterate the
  // richer sources FIRST so dedupe keeps the row with content blocks; the
  // home pool is a tail-end fallback for any activities only present there.
  const seen = new Set<string>();
  const merged: MajourneysActivity[] = [];
  for (const activity of [...exploreResponse, ...bookableResponse, ...bookablePool]) {
    if (seen.has(activity.id)) continue;
    seen.add(activity.id);
    merged.push(activity);
  }

  const rawCities = (data.cities ?? []) as Array<{
    id: string;
    slug: string;
    name: string;
    image?: string | null;
    activitiesCount?: number;
    latitude?: number | null;
    longitude?: number | null;
  }>;
  const cities: DiscoverCity[] = rawCities.map((c) => ({
    id: c.id,
    slug: c.slug,
    name: c.name,
    image: c.image ?? null,
    activitiesCount: c.activitiesCount ?? 0,
    latitude: typeof c.latitude === "number" ? c.latitude : null,
    longitude: typeof c.longitude === "number" ? c.longitude : null,
    // /home doesn't carry these — discover-index does. Default safe.
    discoverHeroLabel: "",
    discoverMapEmbedUrl: "",
    discoverTripPresets: [],
  }));

  const items = merged.map((a) => mapActivity(a, lang)).filter((it) => it.image && it.image.length > 0);
  return { items, cities };
}

/**
 * Pull activities for a given mode (`bookable` or `explore`) via the typed
 * `/activities` endpoint. Returns full ActivityDTOs (with template +
 * contentBlocks + facts + faqs + overviewSections + body + lat/lng) flattened
 * into the MajourneysActivity shape consumed by `mapActivity`.
 */
async function fetchActivitiesByMode(
  mode: "bookable" | "explore",
  lang?: string,
): Promise<MajourneysActivity[]> {
  try {
    const PAGE_SIZE = 100;
    const baseQuery: Record<string, unknown> = { mode, pageSize: PAGE_SIZE };
    if (lang) baseQuery.lang = lang;
    const first = unwrap(
      await api.activities.get({
        query: { ...baseQuery, page: 1 },
      }),
    ) as { rows: Array<Record<string, unknown>>; total: number };
    const rows: Array<Record<string, unknown>> = [...first.rows];
    const totalPages = Math.max(1, Math.ceil((first.total ?? rows.length) / PAGE_SIZE));
    for (let page = 2; page <= totalPages; page++) {
      const next = unwrap(
        await api.activities.get({
          query: { ...baseQuery, page },
        }),
      ) as { rows: Array<Record<string, unknown>> };
      rows.push(...next.rows);
    }
    return rows.map((row) => adaptDtoToMajourneysActivity(row, lang));
  } catch {
    return [];
  }
}

function localized(value: unknown, lang?: string, fallback = ""): string {
  if (typeof value === "string") return value || fallback;
  if (!value || typeof value !== "object") return fallback;
  const o = value as Record<string, string | undefined>;
  if (lang && o[lang]) return o[lang]!;
  return o.en || o.fr || o.ar || fallback;
}

function adaptDtoToMajourneysActivity(dto: Record<string, unknown>, lang?: string): MajourneysActivity {
  const city = (dto.city ?? null) as { id?: string; name?: { en?: string; fr?: string; ar?: string }; slug?: string } | null;
  const category = (dto.category ?? null) as { id?: string; name?: { en?: string; fr?: string; ar?: string }; slug?: string } | null;
  // Prefer the pre-built Cloudflare variant (the legacy migration data has
  // working CF variants but unreliable spacesUrls). Fall back to spacesUrl
  // when no CF variant is available.
  const pickMediaUrl = (m: { spacesUrl?: string; cfImageVariants?: string[] | null } | null | undefined) => {
    if (!m) return undefined;
    if (m.cfImageVariants && m.cfImageVariants.length > 0) return m.cfImageVariants[0];
    return m.spacesUrl;
  };
  const heroMedia = (dto.heroMedia ?? null) as { spacesUrl?: string; cfImageVariants?: string[] | null } | null;
  const heroFromMedia = pickMediaUrl(heroMedia);
  const galleryMediaList = (dto.galleryMedia ?? []) as Array<{ spacesUrl?: string; cfImageVariants?: string[] | null }>;
  const galleryMedia = galleryMediaList.map(pickMediaUrl).filter((u): u is string => Boolean(u));
  const galleryUrls = ((dto.galleryImageUrls ?? []) as string[]) ?? [];
  const heroUrl = (dto.heroImageUrl as string | null | undefined) ?? null;
  const fallbackImages = [heroFromMedia, ...galleryMedia].filter((u): u is string => Boolean(u));
  const images =
    galleryUrls.length > 0
      ? [heroUrl ?? galleryUrls[0]!, ...galleryUrls.filter((u) => u !== heroUrl)]
      : fallbackImages;

  // The `/activities` endpoint serializes coords as strings. Coerce so the
  // discover map can plot them.
  const toNum = (v: unknown): number | null => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  return {
    id: String(dto.id ?? ""),
    slug: String(dto.slug ?? ""),
    title: localized(dto.title, lang),
    subtitle: localized(dto.subtitle, lang),
    overview: localized(dto.overview, lang),
    images,
    price: Number(dto.price ?? 0),
    rating: Number(dto.rating ?? 0),
    latitude: toNum(dto.latitude),
    longitude: toNum(dto.longitude),
    category: category && category.id
      ? { id: category.id, name: localized(category.name, lang), slug: category.slug ?? "" }
      : null,
    city: city && city.id
      ? { id: city.id, name: localized(city.name, lang), slug: city.slug ?? "" }
      : { id: "", name: "", slug: "" },
    mode: (dto.mode as "bookable" | "explore" | undefined) ?? "explore",
    segment: dto.segment as string | undefined,
    mentions: Number(dto.mentions ?? 0),
    template: (dto.template as string | null | undefined) ?? null,
    contentBlocks: (dto.contentBlocks as Record<string, unknown> | undefined) ?? {},
    bodyMarkdown: (dto.bodyMarkdown as string | null | undefined) ?? null,
    heroImageUrl: heroUrl,
    galleryImageUrls: galleryUrls,
    facts: (dto.facts as string[] | undefined) ?? [],
    faqs: (dto.faqs as Array<{ question: string; answer: string }> | undefined) ?? [],
    overviewSections:
      (dto.overviewSections as Array<{ title: string; body: string }> | undefined) ?? [],
  };
}
